import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CommonModal } from '../CommonModal';
import LoadingSpinner from '../client/LoadingSpinner';
import { getDashboardDetails } from '../../services/ApiService';

const AnalyticsDetailsModal = ({ show, setShow, title, type, interval, fromDate, toDate, clientId = null }) => {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (show && type) {
            fetchDetails();
        }
    }, [show, type, interval, fromDate, toDate, clientId]);

    const fetchDetails = async () => {
        try {
            setLoading(true);
            setError(null);
            const result = await getDashboardDetails(type, interval, fromDate, toDate, clientId);
            if (result?.success) {
                setEntries(result?.data || []);
            } else {
                setError(result?.error?.error_message || 'Failed to fetch details');
            }
        } catch (err) {
            setError('An error occurred while fetching details');
            console.error('Error fetching dashboard details:', err);
        } finally {
            setLoading(false);
        }
    };

    const renderTableHeaders = () => {
        const showClientName = !clientId; // Hide client name column when clientId is provided
        
        switch (type) {
            case 'spending':
                return (
                    <>
                        <th>Campaign Name</th>
                        {showClientName && <th>Client Name</th>}
                        <th>Platform</th>
                        <th>Amount</th>
                        <th>Date</th>
                    </>
                );
            case 'earnings':
                return (
                    <>
                        <th>Client Name</th>
                        <th>Amount</th>
                        <th>Date</th>
                    </>
                );
            case 'clients':
                return (
                    <>
                        <th>Client Name</th>
                        <th>Date</th>
                    </>
                );
            case 'users':
                return (
                    <>
                        <th>Name</th>
                        <th>Email</th>
                        {showClientName && <th>Client Name</th>}
                        <th>Date</th>
                    </>
                );
            case 'programs':
                return (
                    <>
                        <th>Program Name</th>
                        {showClientName && <th>Client Name</th>}
                        <th>Date</th>
                    </>
                );
            case 'campaigns':
                return (
                    <>
                        <th>Campaign Name</th>
                        {showClientName && <th>Client Name</th>}
                        {!clientId && <th>Platform</th>}
                        <th>Date</th>
                    </>
                );
            case 'integrations':
                return (
                    <>
                        <th>Platform</th>
                        {showClientName && <th>Client Name</th>}
                        <th>Date</th>
                    </>
                );
            case 'impressions':
                return (
                    <>
                        <th>Campaign Name</th>
                        {showClientName && <th>Client Name</th>}
                        <th>Platform</th>
                        <th>Impressions</th>
                        <th>Date</th>
                    </>
                );
            default:
                return null;
        }
    };

    const renderClientName = (clientId, clientName) => {
        if (clientId && clientName && clientName !== 'N/A') {
            return (
                <Link 
                    to={`/super_admin/clients/${clientId}/insights`}
                    style={{ color: '#7f56d9', textDecoration: 'none' }}
                    onClick={() => setShow(false)}
                >
                    {clientName}
                </Link>
            );
        }
        return <span>{clientName || 'N/A'}</span>;
    };

    const renderTableRow = (entry, index) => {
        const showClientName = !clientId; // Hide client name column when clientId is provided
        
        switch (type) {
            case 'spending':
                return (
                    <tr key={entry.id || index}>
                        <td>{entry.campaign_name || 'N/A'}</td>
                        {showClientName && <td>{renderClientName(entry.client_id, entry.client_name)}</td>}
                        <td>{entry.platform || 'N/A'}</td>
                        <td>${entry.amount?.toLocaleString() || '0.00'}</td>
                        <td>{entry.date || 'N/A'}</td>
                    </tr>
                );
            case 'earnings':
                return (
                    <tr key={entry.id || index}>
                        <td>{renderClientName(entry.client_id, entry.name || entry.client_name)}</td>
                        <td>${entry.amount?.toLocaleString() || '0.00'}</td>
                        <td>{entry.date || 'N/A'}</td>
                    </tr>
                );
            case 'clients':
                return (
                    <tr key={entry.id || index}>
                        <td>{renderClientName(entry.client_id, entry.name || entry.client_name)}</td>
                        <td>{entry.date || 'N/A'}</td>
                    </tr>
                );
            case 'users':
                return (
                    <tr key={entry.id || index}>
                        <td>{entry.name || 'N/A'}</td>
                        <td>{entry.email || 'N/A'}</td>
                        {showClientName && <td>{renderClientName(entry.client_id, entry.client_name)}</td>}
                        <td>{entry.date || 'N/A'}</td>
                    </tr>
                );
            case 'programs':
                return (
                    <tr key={entry.id || index}>
                        <td>{entry.name || 'N/A'}</td>
                        {showClientName && <td>{renderClientName(entry.client_id, entry.client_name)}</td>}
                        <td>{entry.date || 'N/A'}</td>
                    </tr>
                );
            case 'campaigns':
                return (
                    <tr key={entry.id || index}>
                        <td>{entry.name || 'N/A'}</td>
                        {showClientName && <td>{renderClientName(entry.client_id, entry.client_name)}</td>}
                        {!clientId && <td>{entry.platform || 'N/A'}</td>}
                        <td>{entry.date || 'N/A'}</td>
                    </tr>
                );
            case 'integrations':
                return (
                    <tr key={entry.id || index}>
                        <td>{entry.platform || 'N/A'}</td>
                        {showClientName && <td>{renderClientName(entry.client_id, entry.client_name)}</td>}
                        <td>{entry.date || 'N/A'}</td>
                    </tr>
                );
            case 'impressions':
                return (
                    <tr key={entry.id || index}>
                        <td>{entry.campaign_name || 'N/A'}</td>
                        {showClientName && <td>{renderClientName(entry.client_id, entry.client_name)}</td>}
                        <td>{entry.platform || 'N/A'}</td>
                        <td>{entry.impressions?.toLocaleString() || '0'}</td>
                        <td>{entry.date || 'N/A'}</td>
                    </tr>
                );
            default:
                return null;
        }
    };

    const body = (
        <div>
            {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                    <LoadingSpinner />
                </div>
            ) : error ? (
                <div className="alert alert-danger">{error}</div>
            ) : entries.length === 0 ? (
                <div className="alert alert-info">No entries found for the selected time period.</div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-striped table-hover">
                        <thead>
                            <tr>
                                {renderTableHeaders()}
                            </tr>
                        </thead>
                        <tbody>
                            {entries.map((entry, index) => renderTableRow(entry, index))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );

    return (
        <CommonModal
            show={show}
            setShow={setShow}
            title={title}
            body={body}
            size="xl"
            height="70vh"
        />
    );
};

export default AnalyticsDetailsModal;
